import {useTranslation} from "ni18n";
import React, {useEffect} from "react";
import {useDispatch} from "react-redux";
import About from "src/components/v5/CovidPositiveCare/About";
import QuestionAnswers from "src/components/v5/CovidPositiveCare/QuestionAnswers";
import Resources from "src/components/v5/CovidPositiveCare/Resources";
import {ApptReasonId} from "src/constants/apptReasons";
import {SpecialtyId} from "src/constants/specialtyIds";
import {useFaqIndex} from "src/hooks/useFaqIndex";

import {todayTomorrow4, trackToPatient} from "../components/_common/Carbon";
import CoverageMap from "../components/v5/CoverageMap";
import Features from "../components/v5/CovidPositiveCare/Features";
import Hero from "../components/v5/CovidPositiveCare/Hero";
import Faq from "../components/v5/Faq/Faq";
import Layout from "../components/v5/Layout";
import {NavTheme} from "../components/v5/Nav/useNavState";
import {useGetSoonestInPracticeBySpecialtyId} from "../hooks/useGetSoonestTime";
import {actions} from "../store";
import {getSchedulingRedirectUrl} from "../utils/getRedirectUrl";

const CovidPositiveEnrollment = () => {
  const i18n = useTranslation();
  const dispatch = useDispatch();

  const maybeSlot = useGetSoonestInPracticeBySpecialtyId({
    specialtyId: SpecialtyId.VIRTUAL_URGENT_CARE,
  });

  const nextApptSlot = todayTomorrow4(i18n.lang, maybeSlot?.time);

  useEffect(() => {
    dispatch(
      actions.setConfig({
        pageLoaded: true,
      }),
    );
  }, [dispatch]);

  const bookingBtnHandler = () => {
    trackToPatient({ga: {label: "COVID POSITIVE"}});
  };

  const bookingUrl = getSchedulingRedirectUrl({
    apptReasonId: ApptReasonId.COVID_I_TESTED_POSITIVE,
    virtual: true,
    specialtyId: SpecialtyId.VIRTUAL_URGENT_CARE,
  });

  const coveredStates: string[] = [
    "AL",
    "AZ",
    "CA",
    "CO",
    "FL",
    "GA",
    "IL",
    "MA",
    "MI",
    "MD",
    "NJ",
    "NY",
    "NC",
    "NV",
    "OH",
    "OR",
    "PA",
    "TX",
    "VT",
    "VA",
    "WA",
    "DC",
  ];

  const coverageMapProps = {
    coveredStates,
  };

  const faq = useFaqIndex([
    "How much does it cost to enroll in COVID Positive Care?",
    "Why is Carbon Health offering this program?",
    "What happens if a patient develops COVID-related complications while in the program?",
    "Will Carbon Health be providing repeat testing for patients enrolled in the program?",
    "How are mental health referrals handled?",
  ]);

  return (
    <Layout
      pageTitle={i18n.t("Tested positive for COVID-19? Here's what's next | Carbon Health")}
      pageImage="covid-positive-enrollment"
      pageDescription={i18n.t(
        "Having tested positive for coronavirus can be scary. Carbon Health is here to help you figure out next steps while making sure you get the care you need.",
      )}
      navTheme={NavTheme.LIGHT}
    >
      <Hero {...{bookingBtnHandler, bookingUrl, nextApptSlot}} />
      <About {...{nextApptSlot, bookingUrl, bookingBtnHandler}} />
      <Features {...{nextApptSlot, bookingUrl, bookingBtnHandler}} />
      <CoverageMap {...coverageMapProps} />
      <QuestionAnswers />
      <Resources />
      <Faq faq={faq} />
    </Layout>
  );
};

export default CovidPositiveEnrollment;
